import { inject as service } from '@ember/service';
import TransactionalBaseMetricsController from 'cio/pods/transactional/message/overview/base-metrics-controller';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import TransactionalMessageModel, { TransactionalMessageUpdateType } from 'cio/models/transactional-message';
import { TransactionalCodeSnippetLanguage } from 'cio/utils/transactional/code-snippet-request-renderer';
import TagModel from 'cio/models/tag';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';
import { Measure } from 'cio/utils/metrics/common';
import AnalyticsService from 'cio/services/analytics';
import RouterService from '@ember/routing/router-service';

export default class TransactionalMessageOverviewIndexController extends TransactionalBaseMetricsController {
  @service analytics!: AnalyticsService;
  @service router!: RouterService;

  activeSnippetName: TransactionalCodeSnippetLanguage = 'Node.js';

  declare model: TransactionalMessageModel;

  get deliveryMetricChartItems() {
    return [
      {
        label: 'Sent',
        key: 'sent.total',
        dataKey: 'sent',
        isRate: false,
        colorName: 'blue-700',
        gradientName: 'gradient-blue-300',
      },
      {
        label: 'Delivered',
        key: 'delivered.rate',
        dataKey: 'delivered',
        isRate: true,
        colorName: 'blue-700',
        gradientName: 'gradient-blue-300',
      },
      {
        label: 'Opened',
        key: 'opened.rate',
        dataKey: 'opened',
        isRate: true,
        colorName: 'blue-700',
        gradientName: 'gradient-blue-300',
      },
      {
        label: 'Clicked',
        key: 'clicked.rate',
        dataKey: 'clicked',
        isRate: true,
        colorName: 'blue-700',
        gradientName: 'gradient-blue-300',
      },
    ].filter((item) => isDeliveryMeasureEnabledForChannel(item.dataKey as Measure, this.model.type));
  }

  updateSettingTask = task(async (key: keyof TransactionalMessageModel, value: string) => {
    this.analytics.track('transactional', 'edit_started', { editType: key, model: this.model });
    this.model.set('updateType', TransactionalMessageUpdateType.Settings);
    this.model.set(key, value);
    await this.model.save();
  });

  updateDescriptionTask = task(async (value: string) => {
    this.analytics.track('transactional', 'edit_started', { editType: 'description', model: this.model });
    this.model.set('updateType', TransactionalMessageUpdateType.NameAndDescription);
    this.model.set('description', value);
    await this.model.save();
  });

  @action
  onUpdateActiveSnippet(name: TransactionalCodeSnippetLanguage) {
    this.set('activeSnippetName', name);
  }

  @action
  rollbackChanges() {
    this.model.rollbackAttributes();
  }

  @action
  addTag(tag: TagModel) {
    this.analytics.track('transactional', 'edit_started', { editType: 'tags', model: this.model });
    return this.model.addTags([tag]);
  }

  @action
  removeTag(tag: TagModel) {
    this.analytics.track('transactional', 'edit_started', { editType: 'tags', model: this.model });
    return this.model.removeTag(tag);
  }
}

declare module '@ember/controller' {
  interface Registry {
    'transactional.message.overview.index': TransactionalMessageOverviewIndexController;
  }
}
